import type { AdminMetricsDto, AdminMetricsHourCount } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsTile } from './AnalyticsTile';
import { tokens } from '../../../design';
import { hourInMinsk } from '../../../utils/format';

/**
 * Tile 4 — Новые заказы по часам. 24-bar histogram of `ordersByHour`
 * (Minsk-local hour buckets), rendered as flex divs (mockup `.bar-chart`),
 * with the total for the window as the big number and the current Minsk
 * hour bar highlighted. Mirrors the `.t-orders` tile in
 * `design/mockups/admin/dashboard.html`.
 *
 * NO charting dependency — bars are CSS-height flex divs. A zero-filled
 * histogram renders 24 empty (muted) bars, not a crash.
 */

/** Label every Nth hour on the x-axis to avoid a cramped 24-label row. */
const LABEL_EVERY = 3;

const Histogram = ({
  rows,
  max,
  activeHour,
  label,
}: {
  rows: AdminMetricsHourCount[];
  max: number;
  activeHour: number | null;
  label: string;
}): ReactElement => (
  <>
    <Box
      role="img"
      aria-label={label}
      sx={{ mt: 'auto', display: 'flex', alignItems: 'flex-end', gap: '2px', height: 72 }}
    >
      {rows.map((b) => (
        <Box
          key={b.hour}
          sx={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            background: tokens.surface.elevated,
            borderRadius: '3px 3px 0 0',
          }}
        >
          <Box
            sx={{
              height: `${max > 0 ? (b.count / max) * 100 : 0}%`,
              background: b.hour === activeHour ? tokens.brand.primaryHover : tokens.brand.primary,
              borderRadius: '3px 3px 0 0',
            }}
          />
        </Box>
      ))}
    </Box>
    <Box sx={{ mt: 0.75, display: 'flex', gap: '2px', fontSize: 9, fontFamily: tokens.font.mono }}>
      {rows.map((b) => (
        <Box
          key={b.hour}
          sx={{
            flex: 1,
            minWidth: 0,
            textAlign: 'center',
            color: b.hour === activeHour ? tokens.brand.primary : tokens.text.tertiary,
            fontWeight: b.hour === activeHour ? 700 : 400,
          }}
        >
          {b.hour % LABEL_EVERY === 0 ? String(b.hour).padStart(2, '0') : ''}
        </Box>
      ))}
    </Box>
  </>
);

export interface OrdersByHourTileProps {
  data: AdminMetricsDto | undefined;
  isLoading: boolean;
  isError: boolean;
  gridColumn: string;
  gridRow?: string;
}

export const OrdersByHourTile = ({
  data,
  isLoading,
  isError,
  gridColumn,
  gridRow,
}: OrdersByHourTileProps): ReactElement => {
  const { t } = useTranslation();
  const buckets = data?.ordersByHour;
  const hasData = Array.isArray(buckets);
  const rows = buckets ?? [];
  const max = rows.reduce((m, b) => Math.max(m, b.count), 0);
  const total = rows.reduce((sum, b) => sum + b.count, 0);
  const activeHour = hourInMinsk(data?.computedAt);
  const label = t('dashboard.orders.title');

  return (
    <AnalyticsTile
      label={label}
      gridColumn={gridColumn}
      gridRow={gridRow}
      isLoading={isLoading}
      isError={isError}
      hasData={hasData}
    >
      {hasData ? (
        <>
          <Box sx={{ fontFamily: tokens.font.mono, fontSize: 22, fontWeight: 700, lineHeight: 1 }}>
            {total}
            <Box component="span" sx={{ fontSize: 13, fontWeight: 500, ml: 0.5, color: tokens.text.tertiary }}>
              {t('dashboard.orders.unit')}
            </Box>
          </Box>
          <Histogram rows={rows} max={max} activeHour={activeHour} label={label} />
        </>
      ) : null}
    </AnalyticsTile>
  );
};
